import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ExcelExport, ExcelExportColumnProps } from '@progress/kendo-react-excel-export';
import { Grid, GridColumn as Column, GridColumnProps, GridColumnState, GridColumnsStateChangeEvent, GridDataStateChangeEvent, GridToolbar, GridToolbarColumnsChooser, GridCustomCellProps, GridToolbarSpacer } from '@progress/kendo-react-grid';
import { Button, DropDownButton, DropDownButtonItem } from '@progress/kendo-react-buttons';
import dayjs from 'dayjs';
import { paths } from '../../../routes';
import { fetchApi } from '../../../services/api';
import { Title } from '../../../utils/title';
import { parsePartTags } from '../PartTagsInput';
import CreatePartDialog from '../dialogs/CreatePartDialog';
import CreateLocationDialog from '../dialogs/CreateLocationDialog';
import ManageLocationsDialog from '../dialogs/ManageLocationsDialog';
import { plusIcon, arrowRotateCwIcon, fileExcelIcon } from '@progress/kendo-svg-icons';
import { DropdownFilterCellIDName } from 'components/cells/DropdownFilterCellIDName';

enum StockStatus {
  NoStock = 1,
  LowStock = 2,
  InStock = 3,
}

const stockStatusOptions = [
  { ID: StockStatus.NoStock, Name: 'No Stock', Badge: 'badge-danger' },
  { ID: StockStatus.LowStock, Name: 'Low Stock', Badge: 'badge-warning' },
  { ID: StockStatus.InStock, Name: 'In Stock', Badge: 'badge-success' },
];

interface InventoryPartViewModel {
  PartID: number;
  PartNumber: string;
  Description?: string;
  Tags?: string;
  QuantityInStock: number;
  AverageCost: number;
  MinQuantity: number;
  MaxQuantity: number;
  UnitOfMeasure: string;
  LastRestockDate?: string;
  LastUsedDate?: string;
  IsActive: boolean;
  CreatedDateTime: string;
  PrimaryLocationName?: string;
  IsLowStock: boolean;
  Status: StockStatus;
}

interface InventoryResponse {
  Items: {
    Data: InventoryPartViewModel[];
    Total: number;
  };
}

const HIDDEN_COLUMNS_STORAGE_KEY = 'ShopInventory-hiddenColumns';

// The Grid generates its own column ids unless each column declares one, and the columns chooser
// reports visibility keyed by that id. GridColumnProps does not type id, so it is declared here.
type InventoryColumn = GridColumnProps & { id: string };

const DEFAULT_HIDDEN_COLUMNS = ['MinQuantity', 'MaxQuantity'];
const EXPORT_PAGE_SIZE = 10000;

const readHiddenColumns = (): string[] => {
  const stored = localStorage.getItem(HIDDEN_COLUMNS_STORAGE_KEY);
  return stored ? JSON.parse(stored) as string[] : DEFAULT_HIDDEN_COLUMNS;
};

const getEffectiveGridState = (state: GridState, includeRetired: boolean): GridState => includeRetired ? state : {
  ...state,
  filter: {
    logic: 'and',
    filters: [
      ...(state.filter ? [state.filter] : []),
      { field: 'IsActive', operator: 'eq', value: true }
    ]
  }
};

const ShopInventory: React.FC = () => {
  const history = useHistory();
  const excelExport = useRef<ExcelExport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [gridState, setGridState] = useState<GridState>({
    skip: 0,
    take: 25,
    sort: [{ field: 'PartNumber', dir: 'asc' }]
  });
  const [data, setData] = useState<InventoryPartViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [showCreatePartDialog, setShowCreatePartDialog] = useState(false);
  const [showCreateLocationDialog, setShowCreateLocationDialog] = useState(false);
  const [showManageLocationsDialog, setShowManageLocationsDialog] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(readHiddenColumns);

  const loadData = useCallback(async (state: GridState, includeRetired: boolean) => {
    setLoading(true);
    try {
      const effectiveState = getEffectiveGridState(state, includeRetired);
      const response: InventoryResponse = await fetchApi(`/api/ShopInventory/Parts?${toDataSourceRequestString(effectiveState)}`);
      setData(response.Items.Data);
      setTotal(response.Items.Total);
    } catch (error) {
      console.error('Error loading inventory parts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(gridState, showRetired);
  }, [gridState, showRetired, loadData]);

  const onGridStateChange = (event: GridDataStateChangeEvent) => {
    setGridState(event.dataState);
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const exportState = getEffectiveGridState({
        ...gridState,
        skip: 0,
        take: EXPORT_PAGE_SIZE
      }, showRetired);
      const response: InventoryResponse = await fetchApi(`/api/ShopInventory/Parts?${toDataSourceRequestString(exportState)}`);
      const exportData = response.Items.Data.map((part) => ({
        ...part,
        Status: stockStatusOptions.find(x => x.ID === part.Status)?.Name,
        LastRestockDate: part.LastRestockDate ? new Date(part.LastRestockDate) : null,
        LastUsedDate: part.LastUsedDate ? new Date(part.LastUsedDate) : null
      }));
      excelExport.current?.save(exportData);
    } catch (error) {
      console.error('Error exporting inventory parts:', error);
    } finally {
      setExporting(false);
    }
  };

  const onColumnsStateChange = (event: GridColumnsStateChangeEvent) => {
    const hidden = event.columnsState.filter(x => x.hidden).map(x => x.id);
    setHiddenColumns(hidden);
    localStorage.setItem(HIDDEN_COLUMNS_STORAGE_KEY, JSON.stringify(hidden));
  };

  const viewPartDetail = (partId: number) => {
    history.push(paths.ShopInventoryDetail.replace(':partId', partId.toString()));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const StockStatusCell = (props: GridCustomCellProps) => {
    const status = stockStatusOptions.find(x => x.ID === props.dataItem.Status);
    return <td {...props.tdProps}><span className={`badge ${status?.Badge}`}>{status?.Name}</span></td>;
  };

  const QuantityInStockCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps}>{props.dataItem.QuantityInStock} {props.dataItem.UnitOfMeasure}</td>
  );

  const TagsCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps}>
      {parsePartTags(props.dataItem.Tags).map((tag) => (
        <span key={tag} className="badge badge-secondary mr-1">{tag}</span>
      ))}
    </td>
  );

  const onItemCreated = () => {
    loadData(gridState, showRetired); // Refresh the grid
  };

  const PartNumberCell = (props: GridCustomCellProps) => {
    const { dataItem } = props;
    return (
      <td {...props.tdProps}>
        <Button
          fillMode="flat"
          themeColor="primary"
          size="small"
          onClick={() => viewPartDetail(dataItem.PartID)}
        >
          {dataItem.PartNumber}
        </Button>
        {!dataItem.IsActive && <span className="badge badge-secondary ml-1">Retired</span>}
      </td>
    );
  };

  const columns: InventoryColumn[] = [
    { id: 'PartNumber', field: 'PartNumber', title: 'Part Number', cells: { data: PartNumberCell } },
    { id: 'Description', field: 'Description', title: 'Description' },
    { id: 'PrimaryLocationName', field: 'PrimaryLocationName', title: 'Location' },
    { id: 'QuantityInStock', field: 'QuantityInStock', title: 'Qty in Stock', filter: 'numeric', cells: { data: QuantityInStockCell } },
    { id: 'MinQuantity', field: 'MinQuantity', title: 'Min Qty', filter: 'numeric' },
    { id: 'MaxQuantity', field: 'MaxQuantity', title: 'Max Qty', filter: 'numeric' },
    { id: 'AverageCost', field: 'AverageCost', title: 'Avg Cost', format: '{0:c}', filter: 'numeric' },
    { id: 'Tags', field: 'Tags', title: 'Tags', cells: { data: TagsCell }, sortable: false },
    {
      id: 'LastRestockDate',
      field: 'LastRestockDate',
      title: 'Last Restocked',
      filter: 'date',
      cells: { data: (props) => <td {...props.tdProps}>{formatDate(props.dataItem.LastRestockDate)}</td> }
    },
    {
      id: 'LastUsedDate',
      field: 'LastUsedDate',
      title: 'Last Used',
      filter: 'date',
      cells: { data: (props) => <td {...props.tdProps}>{formatDate(props.dataItem.LastUsedDate)}</td> }
    },
    { id: 'Status', field: 'Status', title: 'Status', filter: 'numeric', cells: { data: StockStatusCell, filterCell: (props) => <DropdownFilterCellIDName {...props} data={stockStatusOptions} /> } }
  ];

  const columnsState: GridColumnState[] = columns.map((column) => ({
    id: column.id,
    field: column.field,
    title: column.title,
    hidden: hiddenColumns.includes(column.id)
  }));

  const exportColumns: ExcelExportColumnProps[] = columns
    .filter((column) => column.field && !hiddenColumns.includes(column.id))
    .map((column) => ({
      field: column.field,
      title: column.title,
      cellOptions: column.field === 'AverageCost'
        ? { format: '$#,##0.00' }
        : column.field === 'LastRestockDate' || column.field === 'LastUsedDate'
          ? { format: 'mm/dd/yyyy' }
          : undefined
    }));

  return <>
    <Title string="Shop Inventory" />
    <br />
    <ExcelExport
      ref={excelExport}
      columns={exportColumns}
      fileName={`Shop Inventory ${dayjs().format('YYYY-MM-DD')}.xlsx`}
    />
      <Grid
        data={data}
        showLoader={loading}
        total={total}
        pageable={{ pageSizes: [25, 50, 100, 500] }}
        sortable={true}
        filterable={true}
        {...gridState}
        onDataStateChange={onGridStateChange}
        columnsState={columnsState}
        onColumnsStateChange={onColumnsStateChange}
        scrollable='none'
        dataItemKey="PartID"
      >
        <GridToolbar>
          <Button
            svgIcon={plusIcon}
            themeColor="success"
            onClick={() => setShowCreatePartDialog(true)}
          >
            Part
          </Button>
          <DropDownButton
            text="Locations"
            onItemClick={(e) => {
              if (e.itemIndex === 0) {
                setShowCreateLocationDialog(true);
              } else {
                setShowManageLocationsDialog(true);
              }
            }}
          >
            <DropDownButtonItem text="Add Location" />
            <DropDownButtonItem text="Manage Locations" />
          </DropDownButton>
          <Button
            togglable
            selected={showRetired}
            onClick={() => setShowRetired((prev) => !prev)}
          >
            {showRetired ? 'Hide Retired' : 'Show Retired'}
          </Button>
          <Button
            svgIcon={fileExcelIcon}
            disabled={exporting}
            onClick={exportToExcel}
          >
            {exporting ? 'Exporting...' : 'Excel'}
          </Button>
          <Button
            svgIcon={arrowRotateCwIcon}
            fillMode="flat"
            title="Refresh"
            onClick={() => loadData(gridState, showRetired)}
          />
          <GridToolbarSpacer />
          <GridToolbarColumnsChooser />
        </GridToolbar>
        {columns.map((column) => (
          <Column
            key={column.id}
            {...column}
            hidden={hiddenColumns.includes(column.id)}
          />
        ))}
      </Grid>
    {showCreatePartDialog && <CreatePartDialog
      onClose={() => setShowCreatePartDialog(false)}
      onSuccess={onItemCreated}
    />}
    {showCreateLocationDialog && <CreateLocationDialog
      onClose={() => setShowCreateLocationDialog(false)}
      onSuccess={onItemCreated}
    />}
    {showManageLocationsDialog && <ManageLocationsDialog
      onClose={() => setShowManageLocationsDialog(false)}
      onChanged={onItemCreated}
    />}
  </>;
};

export default ShopInventory;
